import { Divider } from 'primereact/divider';
import { Sidebar, type SidebarProps } from 'primereact/sidebar';
import { classNames } from 'primereact/utils';
import style from '../../../views/employee.module.css';
import {
	paymentStatusTextColor,
	type PaymentStatusUi,
} from '@/modules/vehicle/engine/PaymentStatus';

type Transaction = {
	id: string;
	date: string;
	time: string;
	type: string;
	promo: string;
	promoFunded: string;
	paymentMethod: string;
	amount: number;
	status: string;
	processor: string;
	reference: string;
	preauth: string;
	earningTime: string;
	commission: string;
};

interface Props extends SidebarProps {
	transaction: Transaction;
}

const TransactionDetails = (props: Props) => {
	const { transaction } = props;
	const headerTemplate = (
		<h1 className="text-headline-small text-[28px] font-semibold mt-3">
			TX ID: {transaction.id}
		</h1>
	);

	const listTemplate = (label: string, value: string) => (
		<div className="grid grid-cols-2">
			<div className="text-title-medium text-neutral17 py-2 px-3">
				{label}
			</div>
			<div className="text-title-medium text-neutral50 py-2 px-3">
				{value}
			</div>
		</div>
	);

	return (
		<Sidebar
			{...props}
			position="right"
			draggable={false}
			header={headerTemplate}
			pt={{
				root: () => ({
					className: classNames(
						style.employeeSideSheetTop,
						style.employeeSideSheetBottom,
						style.employeeSideSheet,
						style.employeeSideSheetRoot,
					),
				}),
				header: {
					className: classNames(
						style.employeeSideSheetHeader,
						style.employeeSideSheetTop,
						style.employeeSideSheet,
					),
				},
				content: {
					className: classNames(
						style.employeeSideSheet,
						style.employeeSideSheetBottom,
						style.employeeSideSheetContent,
					),
				},
			}}
		>
			<div className="">
				{/* Xpress Transaction */}
				<div className="pl-3">
					<h3 className="text-title-medium font-semibold text-onSurfaceVariant py-1">
						Xpress Transaction
					</h3>
					<div>
						{listTemplate('Transaction date:', transaction.date)}
						{listTemplate('Transaction time:', transaction.time)}
						{listTemplate('Transaction type:', transaction.type)}
						{listTemplate('Promo:', transaction.promo || '-')}
						{listTemplate('Promo Funded:', transaction.promoFunded)}
						{listTemplate(
							'Payment method:',
							transaction.paymentMethod,
						)}
						{listTemplate(
							'TX amount:',
							`₱${transaction.amount.toFixed(2)}`,
						)}
						<div className="grid grid-cols-2">
							<div className="text-title-medium text-neutral17 py-2 px-3">
								TX status:
							</div>
							<div className="text-title-medium text-neutral50 py-2 px-3">
								<span
									className={paymentStatusTextColor(
										transaction.status as PaymentStatusUi,
									)}
								>
									{transaction.status}
								</span>
							</div>
						</div>
					</div>
				</div>
				<div className="px-4 py-3">
					<Divider
						pt={{
							root: {
								className: classNames(
									style.employeeSideSheetDivider,
								),
							},
						}}
					/>
				</div>
				{/* Payment Processor */}
				<div className="pl-3">
					<h3 className="text-title-medium font-semibold text-onSurfaceVariant py-1">
						Payment Processor
					</h3>
					<div>
						{listTemplate('Partner:', transaction.processor)}
						{listTemplate('TX time:', transaction.time)}
						<div className="grid grid-cols-2">
							<div className="text-title-medium text-neutral17 py-2 px-3">
								Reference:
							</div>
							<div className="text-title-medium text-neutral50 py-2 px-3">
								<span className="truncate">
									{transaction.reference}
								</span>
							</div>
						</div>
						<div className="grid grid-cols-2">
							<div className="text-title-medium text-neutral17 py-2 px-3">
								Status:
							</div>
							<div className="text-title-medium text-neutral50 py-2 px-3">
								<span
									className={paymentStatusTextColor(
										transaction.status as PaymentStatusUi,
									)}
								>
									{transaction.status}
								</span>
							</div>
						</div>
					</div>
				</div>
				<div className="px-4 py-3">
					<Divider
						pt={{
							root: {
								className: classNames(
									style.employeeSideSheetDivider,
								),
							},
						}}
					/>
				</div>
				{/* Order Timeline */}
				<div className="pl-3">
					<h3 className="text-title-medium font-semibold text-onSurfaceVariant py-1">
						Order Timeline
					</h3>
					<p className="text-label-small text-onSurfaceVariant py-1">
						Order Processing
					</p>
					<div>
						{listTemplate('Preauth:', transaction.preauth)}
						{listTemplate(
							'Rider Earning:',
							transaction.earningTime,
						)}
						{listTemplate('Commission:', transaction.commission)}
					</div>
				</div>
			</div>
		</Sidebar>
	);
};

export default TransactionDetails;
